import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ZoomIn, Crop } from "lucide-react";

const SIZE = 288; // preview square
const OUT = 512; // exported square

// Lightweight square image cropper: drag to reposition, slider to zoom, exports a 512x512 PNG.
export function CropperDialog({ file, open, onOpenChange, onCropped, busy }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);

  const cover = useCallback(() => {
    const img = imgRef.current;
    return img ? SIZE / Math.min(img.width, img.height) : 1;
  }, []);

  const clamp = useCallback((z, o) => {
    const img = imgRef.current;
    if (!img) return o;
    const s = cover() * z;
    const w = img.width * s, h = img.height * s;
    return { x: Math.min(0, Math.max(SIZE - w, o.x)), y: Math.min(0, Math.max(SIZE - h, o.y)) };
  }, [cover]);

  const draw = useCallback((z, o) => {
    const c = canvasRef.current, img = imgRef.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, SIZE, SIZE);
    const s = cover() * z;
    ctx.drawImage(img, o.x, o.y, img.width * s, img.height * s);
  }, [cover]);

  useEffect(() => {
    if (!file) { setReady(false); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const start = { x: (SIZE - img.width * (SIZE / Math.min(img.width, img.height))) / 2, y: (SIZE - img.height * (SIZE / Math.min(img.width, img.height))) / 2 };
      setZoom(1);
      setOffset(start);
      setReady(true);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => { if (ready) draw(zoom, offset); }, [ready, zoom, offset, draw]);

  const onDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { px: e.clientX, py: e.clientY, ...offset };
  };
  const onMove = (e) => {
    if (!dragRef.current) return;
    setOffset(clamp(zoom, { x: dragRef.current.x + (e.clientX - dragRef.current.px), y: dragRef.current.y + (e.clientY - dragRef.current.py) }));
  };
  const onUp = () => { dragRef.current = null; };

  const onZoom = (v) => {
    const z = Number(v);
    setZoom(z);
    setOffset((o) => clamp(z, o));
  };

  const confirm = () => {
    const img = imgRef.current;
    if (!img) return;
    const k = OUT / SIZE, s = cover() * zoom * k;
    const oc = document.createElement("canvas");
    oc.width = OUT; oc.height = OUT;
    const octx = oc.getContext("2d");
    octx.fillStyle = "#ffffff";
    octx.fillRect(0, 0, OUT, OUT);
    octx.drawImage(img, offset.x * k, offset.y * k, img.width * s, img.height * s);
    oc.toBlob((b) => onCropped(b), "image/png", 0.95);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Crop className="h-5 w-5 text-teal-700" /> Crop logo</DialogTitle>
          <DialogDescription>Drag to reposition and zoom to frame a clean square.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <div className="relative overflow-hidden rounded-xl border border-slate-200" style={{ width: SIZE, height: SIZE }}>
            <canvas
              ref={canvasRef}
              width={SIZE}
              height={SIZE}
              data-testid="cropper-canvas"
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerLeave={onUp}
              className="cursor-grab touch-none active:cursor-grabbing"
            />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-teal-500/30" />
          </div>
          <div className="flex w-full items-center gap-3">
            <ZoomIn className="h-4 w-4 text-slate-400" />
            <input data-testid="cropper-zoom" type="range" min="1" max="3" step="0.01" value={zoom} onChange={(e) => onZoom(e.target.value)} className="w-full accent-teal-600" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="cropper-confirm" disabled={!ready || busy} className="bg-teal-700 hover:bg-teal-800" onClick={confirm}>{busy ? "Uploading…" : "Use logo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
